import axios, { AxiosInstance, AxiosError } from 'axios';
import { message } from 'antd';
import { getLocalBackendOfflineHint, getResolvedApiBaseUrl } from '../utils/apiBaseUrl';
import { clearAuthData, getAuthItem, setAuthItem } from '../utils/authStorage';

const OFFLINE_TOAST_INTERVAL_MS = 10000;
let lastOfflineToastAt = 0;

const notifyBackendOffline = () => {
  const now = Date.now();
  if (now - lastOfflineToastAt < OFFLINE_TOAST_INTERVAL_MS) return;
  lastOfflineToastAt = now;
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  message.warning(
    isLocal
      ? getLocalBackendOfflineHint()
      : '无法连接服务器，请稍后重试或联系平台管理员',
  );
};

// 创建 axios 实例
const api: AxiosInstance = axios.create({
  /** 与 backend/src/main.ts 默认 PORT=8000 对齐；若后端使用 PORT=3000 请配置 REACT_APP_API_BASE_URL */
  baseURL: getResolvedApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<any> | null = null;

// 请求拦截器：添加 token
api.interceptors.request.use(
  (config) => {
    const token = getAuthItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete (config.headers as any)['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：统一错误处理
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<any>) => {
    // 处理错误
    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = error.config?.url || '';
      
      switch (status) {
        case 401:
          // 登录/刷新接口返回401时由页面处理
          if (
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/login-by-sms') ||
            requestUrl.includes('/auth/refresh')
          ) {
            // 不在这里显示，由 Login 组件 catch 处理
          } else {
            const originalRequest: any = error.config || {};
            if (!originalRequest._retry && getAuthItem('refresh_token')) {
              originalRequest._retry = true;
              if (!refreshPromise) {
                refreshPromise = api
                  .post('/auth/refresh', {
                    refresh_token: getAuthItem('refresh_token'),
                  })
                  .finally(() => {
                    refreshPromise = null;
                  });
              }
              return refreshPromise
                .then((resp: any) => {
                  const d = resp?.data || {};
                  if (d.access_token) setAuthItem('access_token', d.access_token);
                  if (d.refresh_token) setAuthItem('refresh_token', d.refresh_token);
                  if (d.session_id) setAuthItem('session_id', d.session_id);
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${d.access_token}`;
                  return api(originalRequest);
                })
                .catch(() => {
                  message.error('未授权，请重新登录');
                  clearAuthData();
                  window.location.href = '/login';
                  return Promise.reject(error);
                });
            } else {
              message.error('未授权，请重新登录');
              clearAuthData();
              window.location.href = '/login';
            }
          }
          break;
        case 403:
          if (
            !requestUrl.includes('/auth/login') &&
            !requestUrl.includes('/auth/login-by-sms') &&
            !requestUrl.includes('/auth/refresh')
          ) {
            const agreementDenied =
              typeof data?.message === 'string' &&
              (data.message.includes('月结三方协议') || data.message.includes('严选月结'));
            if (agreementDenied) {
              // 未同意协议：由严选弹窗引导，勿当会话失效重跑 initAuth
              message.warning(data.message);
              break;
            }
            window.dispatchEvent(new CustomEvent('lce:auth-forbidden'));
          }
          message.error(data?.message || '没有权限访问该资源');
          break;
        case 413:
          message.error(
            data?.message ||
              (typeof data === 'string' ? data : null) ||
              '上传文件过大，请压缩后重试（单文件建议不超过 80MB）',
          );
          break;
        case 429:
          if (!requestUrl.includes('/im/')) {
            message.error('操作过于频繁，请稍后再试');
          }
          break;
        case 404:
          message.error('请求的资源不存在');
          break;
        case 500:
          // 登录/注册等由页面 catch 统一提示，避免与拦截器各弹一次（重复两条相同 toast）
          if (
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/login-by-sms') ||
            requestUrl.includes('/auth/register') ||
            requestUrl.includes('/auth/send-sms-code')
          ) {
            break;
          }
          message.error(
            data?.message ||
              (typeof data === 'string' ? data : null) ||
              '服务器错误，请稍后重试',
          );
          break;
        default:
          if (
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/login-by-sms') ||
            requestUrl.includes('/auth/register') ||
            requestUrl.includes('/auth/send-sms-code')
          ) {
            break;
          }
          message.error(data?.message || '请求失败，请稍后重试');
      }
    } else if (error.request) {
      const timedOut =
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        /timeout of \d+ms exceeded/i.test(String(error.message || ''));
      // axios 超时没有 response，不能当成后端挂了（预匹配等长请求会误报「无法连接后端」）
      if (!timedOut) notifyBackendOffline();
    } else {
      message.error('请求配置错误');
    }
    
    return Promise.reject(error);
  }
);

export default api;
